import { Building, CheckSquare,Loader2 } from 'lucide-react';

// Loading genérico
export function Loading({ size = 'md', text = 'Carregando...' }: {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
}) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };

  return (
    <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
      <Loader2 className={`animate-spin ${sizeClasses[size]}`} />
      <span className="font-ageo-regular text-sm">{text}</span>
    </div>
  );
}

// Loading para página inteira
export function PageLoading({ message = 'Verificando acesso...' }: { message?: string }) {
  return (
    <div className="min-h-screen bg-background dark:bg-gray-900 flex items-center justify-center transition-colors">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Building className="w-8 h-8 text-primary dark:text-blue-400 animate-pulse" />
        </div>
        <Loading size="lg" text={message} />
        <div className="mt-4">
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 bg-primary dark:bg-blue-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-primary dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-primary dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Loading inline para botões
export function ButtonLoading({ text = 'Carregando...' }: { text?: string }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </div>
  );
}

// Loading para cards/seções
export function SectionLoading({ 
  title = 'Carregando dados...', 
  icon: Icon = CheckSquare 
}: { 
  title?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="bg-background dark:bg-gray-900 rounded-lg shadow-md border border-secondary dark:border-gray-700 p-6">
      <div className="flex items-center justify-center gap-3 text-gray-600 dark:text-gray-400">
        <Icon className="w-5 h-5 animate-pulse" />
        <span className="font-ageo-regular">{title}</span>
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    </div>
  );
}

// Skeleton para listas
export function SkeletonItem({ lines = 2 }: { lines?: number }) {
  return (
    <div className="animate-pulse">
      <div className="bg-gray-200 dark:bg-gray-700 h-4 rounded w-3/4 mb-2"></div>
      {Array.from({ length: lines - 1 }).map((_, i) => (
        <div key={i} className="bg-gray-200 dark:bg-gray-700 h-3 rounded w-1/2 mb-1"></div>
      ))}
    </div>
  );
}

export function SkeletonList({ items = 3, lines = 2 }: { items?: number; lines?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="bg-background dark:bg-gray-900 rounded-lg border border-secondary dark:border-gray-700 p-4">
          <SkeletonItem lines={lines} />
        </div>
      ))}
    </div>
  );
}

// Loading para tabelas
export function TableLoading({ columns = 4, rows = 5 }: { columns?: number; rows?: number }) {
  return (
    <div className="animate-pulse">
      {/* Header */}
      <div className="flex gap-4 mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        {Array.from({ length: columns }).map((_, i) => (
          <div key={i} className="flex-1">
            <div className="bg-gray-200 dark:bg-gray-700 h-4 rounded"></div>
          </div>
        ))}
      </div>
      
      {/* Rows */}
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex gap-4 p-3 border-b border-secondary dark:border-gray-700">
            {Array.from({ length: columns }).map((_, j) => (
              <div key={j} className="flex-1">
                <div className="bg-gray-200 dark:bg-gray-700 h-3 rounded"></div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Loading específico para PWA
export function PWALoading() {
  return (
    <div className="fixed inset-0 bg-background dark:bg-gray-900 flex items-center justify-center z-50">
      <div className="text-center">
        <div className="w-20 h-20 bg-primary/10 dark:bg-blue-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
          <Building className="w-10 h-10 text-primary dark:text-blue-400" />
        </div>
        
        <h1 className="text-2xl font-ageo-bold text-foreground dark:text-gray-100 mb-2">
          ACOR ERP
        </h1>
        
        <p className="text-gray-600 dark:text-gray-400 font-ageo-regular mb-6">
          Carregando aplicação...
        </p>
        
        <div className="flex items-center justify-center gap-2 mb-4">
          <Loader2 className="h-5 w-5 animate-spin text-primary dark:text-blue-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Iniciando sistema</span>
        </div>
        
        {/* Barra de progresso animada */}
        <div className="w-48 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full h-1 overflow-hidden">
          <div className="bg-primary dark:bg-blue-400 h-full rounded-full animate-pulse" style={{
            animation: 'loading-bar 2s ease-in-out infinite'
          }}></div>
        </div>
        
        <style>{`
          @keyframes loading-bar {
            0% { width: 0%; margin-left: 0%; }
            50% { width: 75%; margin-left: 12.5%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>
      </div>
    </div>
  );
}